import {
  CaptchaProvider,
  CloudflareAction,
  type CloudflareView,
  type CloudflareZone,
  type IntegrationName,
  type IntegrationsView,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { needsReauth } from '../api/client.js';
import {
  cloudflareZones,
  getIntegrations,
  removeIntegration,
  saveCaptcha,
  saveCloudflare,
  saveCti,
  syncCloudflareNow,
} from '../api/integrations.js';
import { useReauth } from '../auth/reauth-context.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * Integrations (P11.9.3, P11.9.5, P11.9.6): the outside services Bartizo holds a
 * secret for — the captcha keys the web bouncer's challenge shows, Cloudflare for
 * bans at the edge, CrowdSec CTI for an address's reputation.
 *
 * Every secret is write-only: the page shows whether one is stored, never the
 * value. Saving or removing asks for the password again (section 17), as the AI
 * page does for provider keys.
 */

const errorText = (caught: unknown, fallback: string): string =>
  needsReauth(caught)
    ? 'That needed a fresh confirmation. Try again.'
    : caught instanceof Error
      ? caught.message
      : fallback;

const PROVIDER_LABEL: Record<CaptchaProvider, string> = {
  turnstile: 'Cloudflare Turnstile',
  hcaptcha: 'hCaptcha',
  recaptcha: 'Google reCAPTCHA',
};

function useSave<T>(save: (input: T) => Promise<IntegrationsView>) {
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const mutation = useMutation({
    mutationFn: save,
    onSuccess: (view) => {
      queryClient.setQueryData(['integrations'], view);
      setError(null);
      setSaved(true);
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(errorText(caught, 'Could not save that.'));
    },
  });
  const submit = async (input: T): Promise<void> => {
    setSaved(false);
    if (await ensureReauthenticated()) mutation.mutate(input);
  };
  return { submit, busy: mutation.isPending, error, saved };
}

function RemoveButton({ name, label }: { name: IntegrationName; label: string }): ReactNode {
  const remove = useSave(() => removeIntegration(name));
  return (
    <>
      <Button
        variant="danger"
        busy={remove.busy}
        busyLabel="Removing…"
        onClick={() => {
          void remove.submit(undefined);
        }}
      >
        Remove {label}
      </Button>
      {remove.error !== null && <Alert tone="danger">{remove.error}</Alert>}
    </>
  );
}

export function IntegrationsPage(): ReactNode {
  const integrations = useQuery({ queryKey: ['integrations'], queryFn: getIntegrations });

  return (
    <div className="page">
      <PageHeader
        title="Integrations"
        description="Outside services Bartizo works with. Keys and tokens are stored encrypted and never shown again."
      />
      {integrations.isError && (
        <Alert tone="danger" title="Could not load the integrations">
          Try again in a moment.
        </Alert>
      )}
      {integrations.data !== undefined && (
        <>
          <CaptchaCard view={integrations.data} />
          <CloudflareCard view={integrations.data.cloudflare} />
          <CtiCard view={integrations.data} />
        </>
      )}
    </div>
  );
}

function CaptchaCard({ view }: { view: IntegrationsView }): ReactNode {
  const current = view.captcha;
  const [provider, setProvider] = useState<CaptchaProvider>(current?.provider ?? 'turnstile');
  const [siteKey, setSiteKey] = useState(current?.siteKey ?? '');
  const [secretKey, setSecretKey] = useState('');
  const save = useSave(saveCaptcha);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    void save.submit({
      provider,
      siteKey: siteKey.trim(),
      ...(secretKey === '' ? {} : { secretKey: secretKey.trim() }),
    });
  };

  return (
    <Card
      title="Captcha for the challenge"
      description="The keys the web bouncer shows a challenge with. Switch the challenge on in a server's or group's web settings."
      aside={
        current === null ? (
          <span className="badge badge-muted">Not set</span>
        ) : (
          <span className="badge badge-success">Set</span>
        )
      }
    >
      <form className="stack" onSubmit={submit}>
        <div className="filters">
          <SelectField
            label="Service"
            value={provider}
            onChange={(event) => {
              const parsed = CaptchaProvider.safeParse(event.target.value);
              if (parsed.success) setProvider(parsed.data);
            }}
          >
            {CaptchaProvider.options.map((option) => (
              <option key={option} value={option}>
                {PROVIDER_LABEL[option]}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Site key"
            required
            value={siteKey}
            onChange={(event) => {
              setSiteKey(event.target.value);
            }}
          />
          <TextField
            label="Secret key"
            type="password"
            autoComplete="off"
            hint={current?.hasSecret === true ? 'Stored. Leave empty to keep it.' : undefined}
            required={current?.hasSecret !== true}
            value={secretKey}
            onChange={(event) => {
              setSecretKey(event.target.value);
            }}
          />
        </div>
        <span className="row">
          <Button type="submit" busy={save.busy} busyLabel="Saving…">
            Save
          </Button>
          {current !== null && <RemoveButton name="captcha" label="the keys" />}
        </span>
        {save.error !== null && <Alert tone="danger">{save.error}</Alert>}
        {save.saved && (
          <Alert tone="success">
            Saved. Servers with the challenge on pick the keys up within the hour.
          </Alert>
        )}
      </form>
    </Card>
  );
}

function CloudflareCard({ view }: { view: CloudflareView | null }): ReactNode {
  const [enabled, setEnabled] = useState(view?.enabled ?? true);
  const [accountId, setAccountId] = useState(view?.accountId ?? '');
  const [token, setToken] = useState('');
  const [action, setAction] = useState<CloudflareAction>(view?.action ?? 'block');
  const [includeServerBans, setIncludeServerBans] = useState(view?.includeServerBans ?? true);
  const [zones, setZones] = useState<CloudflareZone[]>(view?.zones ?? []);
  const [available, setAvailable] = useState<CloudflareZone[] | null>(null);
  const [zoneError, setZoneError] = useState<string | null>(null);
  const save = useSave(saveCloudflare);
  const queryClient = useQueryClient();

  const findZones = useMutation({
    mutationFn: () => cloudflareZones(accountId.trim(), token.trim()),
    onSuccess: (answer) => {
      setAvailable(answer.zones);
      setZoneError(null);
    },
    onError: (caught: unknown) => {
      setZoneError(errorText(caught, 'Cloudflare could not be asked.'));
    },
  });
  const sync = useMutation({
    mutationFn: syncCloudflareNow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    void save.submit({
      enabled,
      accountId: accountId.trim(),
      action,
      zones,
      includeServerBans,
      ...(token === '' ? {} : { token: token.trim() }),
    });
  };

  const toggleZone = (zone: CloudflareZone): void => {
    setZones((current) =>
      current.some((each) => each.id === zone.id)
        ? current.filter((each) => each.id !== zone.id)
        : [...current, zone],
    );
  };

  return (
    <Card
      title="Cloudflare"
      description="Bans at the edge: the fleet blocklist — and, if you choose, the bans on servers behind Cloudflare — kept in one Cloudflare IP list, with one Bartizo rule in each zone you pick. Your own rules are never changed."
      aside={
        view === null ? (
          <span className="badge badge-muted">Not set</span>
        ) : view.sync.error !== null ? (
          <span className="badge badge-danger">Failing</span>
        ) : view.enabled ? (
          <span className="badge badge-success">On</span>
        ) : (
          <span className="badge badge-muted">Off</span>
        )
      }
    >
      {view !== null && (
        <p className="text-sm">
          {view.sync.syncedAt === null
            ? 'Not synced yet; it runs every ten minutes.'
            : `Last synced ${formatRelative(view.sync.syncedAt)}: ${view.sync.addresses.toLocaleString('en-US')} addresses${view.sync.truncated ? ' (the list is full; the most recently active are kept)' : ''}, rule in ${view.sync.rules.length === 0 ? 'no zone' : view.sync.rules.join(', ')}.`}
          {view.sync.error !== null && (
            <span className="cell-sub">Cloudflare said: {view.sync.error}</span>
          )}
        </p>
      )}
      <form className="stack" onSubmit={submit}>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
            }}
          />
          Keep the Cloudflare list (off empties it)
        </label>
        <div className="filters">
          <TextField
            label="Account ID"
            hint="32 characters, on the account's overview page."
            required
            value={accountId}
            onChange={(event) => {
              setAccountId(event.target.value);
            }}
          />
          <TextField
            label="API token"
            type="password"
            autoComplete="off"
            hint={
              view?.hasToken === true
                ? 'Stored. Leave empty to keep it.'
                : 'Permissions: Account Filter Lists Edit, Zone WAF Edit — nothing else.'
            }
            required={view?.hasToken !== true}
            value={token}
            onChange={(event) => {
              setToken(event.target.value);
            }}
          />
          <SelectField
            label="An address on the list is"
            value={action}
            onChange={(event) => {
              const parsed = CloudflareAction.safeParse(event.target.value);
              if (parsed.success) setAction(parsed.data);
            }}
          >
            <option value="block">Blocked</option>
            <option value="managed_challenge">Challenged</option>
          </SelectField>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeServerBans}
            onChange={(event) => {
              setIncludeServerBans(event.target.checked);
            }}
          />
          Also send CrowdSec’s bans from servers behind Cloudflare
        </label>
        <section>
          <h3>Zones</h3>
          <span className="row">
            <Button
              variant="secondary"
              busy={findZones.isPending}
              busyLabel="Asking Cloudflare…"
              disabled={accountId.trim() === '' || (token === '' && view?.hasToken !== true)}
              onClick={() => {
                findZones.mutate();
              }}
            >
              Find the zones
            </Button>
          </span>
          {zoneError !== null && <Alert tone="danger">{zoneError}</Alert>}
          <ul className="stack">
            {[
              ...(available ?? []),
              ...zones.filter((zone) => !(available ?? []).some((each) => each.id === zone.id)),
            ].map((zone) => (
              <li key={zone.id}>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={zones.some((each) => each.id === zone.id)}
                    onChange={() => {
                      toggleZone(zone);
                    }}
                  />
                  {zone.name}
                </label>
              </li>
            ))}
          </ul>
        </section>
        <span className="row">
          <Button type="submit" busy={save.busy} busyLabel="Saving…">
            Save
          </Button>
          {view !== null && (
            <Button
              variant="secondary"
              busy={sync.isPending}
              busyLabel="Syncing…"
              onClick={() => {
                sync.mutate();
              }}
            >
              Sync now
            </Button>
          )}
          {view !== null && <RemoveButton name="cloudflare" label="Cloudflare" />}
        </span>
        {save.error !== null && <Alert tone="danger">{save.error}</Alert>}
        {save.saved && (
          <Alert tone="success">Saved. The list is brought up to date within ten minutes.</Alert>
        )}
      </form>
    </Card>
  );
}

function CtiCard({ view }: { view: IntegrationsView }): ReactNode {
  const current = view.cti;
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [key, setKey] = useState('');
  const save = useSave(saveCti);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    void save.submit({ enabled, ...(key === '' ? {} : { key: key.trim() }) });
  };

  return (
    <Card
      title="CrowdSec CTI"
      description="An address's reputation from CrowdSec — classifications, behaviours, how widely it attacks — on its page. Asked once per address when it is opened and kept a week; advice only, it never bans."
      aside={
        current === null ? (
          <span className="badge badge-muted">Not set</span>
        ) : current.enabled ? (
          <span className="badge badge-success">On</span>
        ) : (
          <span className="badge badge-muted">Off</span>
        )
      }
    >
      <form className="stack" onSubmit={submit}>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
            }}
          />
          Look addresses up
        </label>
        <TextField
          label="CTI API key"
          type="password"
          autoComplete="off"
          hint={
            current?.hasKey === true
              ? 'Stored. Leave empty to keep it.'
              : 'From the CrowdSec Console, Settings → CTI API keys. The free tier has a small daily quota.'
          }
          required={current?.hasKey !== true}
          value={key}
          onChange={(event) => {
            setKey(event.target.value);
          }}
        />
        <span className="row">
          <Button type="submit" busy={save.busy} busyLabel="Saving…">
            Save
          </Button>
          {current !== null && <RemoveButton name="crowdsec_cti" label="the key" />}
        </span>
        {save.error !== null && <Alert tone="danger">{save.error}</Alert>}
        {save.saved && <Alert tone="success">Saved.</Alert>}
      </form>
    </Card>
  );
}
